import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { Input, Tooltip } from "antd";
import { makeRequest } from '@utils/helpers';
import { addUserCaseChatReq, getUserCaseChatReq, getUserCasesIdReq } from "../requests";
import moment from "moment";
import { isStlFile } from "../stlFile";
import { fileUrl } from "@utils/axios";
import { safeHtml } from "@utils/safeHtml";
import "../chat.css";

/**
 * Case chat.
 *
 * Message kinds come back as msg_type: 0 is a person, 1 and 2 are system
 * notices. Those used to be a mint and a salmon background on the whole
 * bubble, which read as "good" and "error" when they only mean "the
 * system said this". They are now one quiet notice style, with the
 * author's own messages aligned right the way every chat behaves.
 *
 * The chat is for talking about a case, not for moving its files. An
 * STL is an upper and a lower arch and routinely runs to hundreds of
 * megabytes, which is not something to push through a chat box; the
 * case's own "Submit STL file" panel takes a link to it instead, and
 * posts it here. Messages sent back when files could be attached still
 * render their attachment.
 */

const downloadUrl = (attachment) => fileUrl(attachment.path);
const UserCaseChatBox = ({ setChatOpen }) => {
  const [errors, setErrors] = useState(false);
  const [message, setMessage] = useState("");
  // Starts true and stays true across both opening requests: the case
  // list has to answer before the first case's messages can even be
  // asked for, and letting the flag drop in between showed "No messages
  // on this case yet" over a conversation that was still loading.
  const [loader, setLoader] = useState(true);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [caseIds, setCaseIds] = useState([]);
  const [currentCase, setCurrentCase] = useState(null);
  const [failure, setFailure] = useState("");
  const chatEndRef = useRef(null);

  useEffect(() => {
    getCasesIds();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentCase) getCaseMessage();
  }, [currentCase]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCaseMessage = () => {
    makeRequest(setLoader, getUserCaseChatReq, currentCase, onSuccessData, onError);
  };

  const onSuccessData = (data) => setMessages(data);

  const getCasesIds = () => {
    // No loader of its own — see the note on `loader` above. It still
    // goes through makeRequest so a 401 signs the user out as usual.
    makeRequest(() => {}, getUserCasesIdReq, null, onSuccessId, onIdsError);
  };

  const onSuccessId = (data) => {
    setCaseIds(data);
    if (data && data.length > 0) {
      // getCaseMessage takes the wait from here.
      setCurrentCase(data[0].id);
    } else {
      setLoader(false);
    }
  };

  const onIdsError = (data, res) => {
    setLoader(false);
    onError(data, res);
  };

  const onFinish = () => {
    if (!message || !message.trim()) {
      setErrors(true);
      return;
    }
    setFailure("");

    makeRequest(setSending, addUserCaseChatReq,
      { caseId: currentCase, message }, onSuccess, onError);
  };

  const onSuccess = (data) => {
    setMessage("");
    setMessages((prev) => [...prev, data]);
  };

  const onError = (data, res) => {
    setFailure((res && res.msg) || "That didn't send. Please try again.");
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onFinish();
    }
  };

  const activeCase = caseIds.find((each) => each.id === currentCase);

  return (
    <section className="chat" aria-label="Case chat">
      <header className="chat-head">
        <span className="chat-head-mark">
          <Icon icon="tabler:message-circle" width={ 18 } />
        </span>
        <span className="chat-head-text">
          <strong>Case chat</strong>
          <span>{ activeCase ? activeCase.case_number : 'Pick a case' }</span>
        </span>
        <button type="button" className="chat-close" onClick={ () => setChatOpen(false) } aria-label="Close chat">
          <Icon icon="tabler:x" width={ 18 } />
        </button>
      </header>

      { caseIds && caseIds.length > 0 &&
        <div className="chat-cases" role="tablist" aria-label="Cases">
          { caseIds.map((each) => (
            <button
              key={ each.id }
              type="button"
              role="tab"
              aria-selected={ each.id === currentCase }
              className={ `chat-case ${each.id === currentCase ? 'is-active' : ''}` }
              onClick={ () => setCurrentCase(each.id) }
            >
              { each.case_number }
            </button>
          )) }
        </div> }

      <div className="chat-log">
        { loader && messages.length === 0
          ? [0, 1, 2].map((i) => (
            <div className="chat-skel" key={ i } style={ { width: `${70 - i * 12}%` } } />
          ))
          : messages.length === 0
            ? <div className="chat-empty">
              <Icon icon="tabler:message-2" width={ 26 } />
              <p>No messages on this case yet. Start the conversation below.</p>
            </div>
            : messages.map((each, index) => {
              const isSystem = Number(each.msg_type) !== 0;
              return (
                <article className={ `chat-msg ${isSystem ? 'is-system' : ''}` } key={ each.id || index }>
                  { !isSystem &&
                    <span className="chat-msg-avatar">
                      { (each.name || '?').charAt(0).toUpperCase() }
                    </span> }
                  <div className="chat-msg-body">
                    <div className="chat-msg-meta">
                      <span className="chat-msg-author">
                        { isSystem
                          ? <><Icon icon="tabler:info-circle" width={ 14 } /> System</>
                          : each.name }
                      </span>
                      <Tooltip title={ moment(each.created_at).format('DD MMM YYYY, HH:mm') }>
                        <span className="chat-msg-when">{ moment(each.created_at).fromNow() }</span>
                      </Tooltip>
                    </div>
                    <div className="chat-msg-text"
                      dangerouslySetInnerHTML={ safeHtml(each.message) } />
                    { each.attachment &&
                      <a
                        className={ `chat-file ${isStlFile(each.attachment.name) ? 'is-stl' : ''}` }
                        href={ downloadUrl(each.attachment) }
                        target="_blank"
                        rel="noreferrer"
                        download={ each.attachment.name }
                      >
                        <Icon icon={ isStlFile(each.attachment.name) ? "tabler:cube" : "tabler:paperclip" } width={ 16 } />
                        <span className="chat-file-name">{ each.attachment.name }</span>
                        <Icon icon="tabler:download" width={ 15 } />
                      </a> }
                  </div>
                </article>
              );
            }) }
        <div ref={ chatEndRef } />
      </div>

      <div className="chat-compose">
        <Input.TextArea
          value={ message }
          onKeyDown={ handleKeyDown }
          onChange={ (e) => { setMessage(e.target.value); setErrors(false); } }
          autoSize={ { minRows: 1, maxRows: 4 } }
          placeholder="Write a message"
          status={ errors ? 'error' : '' }
          aria-label="Message"
        />
        <button
          type="button"
          className="chat-send"
          onClick={ onFinish }
          disabled={ sending || !currentCase }
          aria-label="Send message"
        >
          <Icon icon={ sending ? "tabler:loader-2" : "tabler:send" } width={ 18 }
            className={ sending ? 'is-spinning' : '' } />
        </button>
      </div>
      { errors && <p className="chat-error">Write a message before sending.</p> }
      { failure && <p className="chat-error">{ failure }</p> }
    </section>
  );
};

export default UserCaseChatBox;
